/* Copyright (c) 2020-present CloudIO Inc */

import { camelCase } from 'lodash-es';
import {
  controlledBy,
  displayName,
  dsAppUidProp,
  getNextAlias,
  hideDirtyIndicator,
} from '../DataStore/DataStoreManager';
import { Platform } from '../../platform/Platform';
import { getManager } from '../../registry/Manager';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './ReadOnlyDataStore';
import { emptyFunction } from '@cloudio-saas/statex';

const ReadOnlyDataStoreManager: ComponentManagerFn<'ReadOnlyDataStore'> = (
  platform: Platform,
): ComponentManager<'ReadOnlyDataStore'> => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'ReadOnlyDataStore'>(platform, 'ReadOnlyDataStore'),
    defaultValue,
    replaceableWith: ['DataStore', 'TransientDataStore', 'LocalDataStore'],
    isChildAllowed: (page, item, childType) =>
      (['TriggerBeforeQuery', 'TriggerAfterQuery'] as ItemType[]).includes(
        childType,
      ),
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
          dataSource: '',
          alias: '',
        };
      }
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      maxRows: {
        label: 'Max Rows',
        type: 'number',
        min: 20,
        max: 2000,
        step: 20,
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      usePagination: {
        label: 'Use Pagination',
        type: 'boolean',
        helpText: 'Check this to use pagination component on this store.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      maskPageOnQuery: {
        label: 'Mask Page On Query',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the query request on store completed.',
      },
      doNotMonitorQueryRequest: {
        label: 'Do not Monitor Query Request',
        type: 'boolean',
        helpText: 'Check to disable monitoring query request.',
      },
      controlledBy,
      hideDirtyIndicator,
    },
    category: 'Data',
    name: 'Readonly Store',
    canHaveChildren: () => true,
    generateShortId: (item) => item.alias,
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'hideDirtyIndicator':
          return false;
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        default:
          return !!item.alias;
      }
    },
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    icon: { iconName: 'database' },
    description: `**Overview**<br/>
The ReadOnly Data Store is a specialized version of the standard Data Store designed exclusively for fetching and displaying data. Unlike a regular Data Store, it strictly forbids any modification to the data—records cannot be inserted, updated, or deleted.
Use this component for "Reference Data" (e.g., lists of Countries, Currencies, or Status Codes) or "Reporting Views" where you want to ensure that the user interface cannot accidentally alter the source of truth.

**1. Core Philosophy: Safety by Design**<br/>
The primary feature of this component is what it removes. By stripping away all write capabilities, it simplifies the architecture and signaling of your page.
* **No Write Operations:** It effectively ignores any attempt to save() or delete().
* **No "Dirty" State:** Since data cannot change, the store never enters a "Dirty" (unsaved changes) state. Consequently, it never triggers "Unsaved Changes" warnings when a user navigates away.
* **Simplified Triggers:** It only supports Query triggers (BeforeQuery, AfterQuery). It does not allow Save or Delete triggers, making the logic flow predictable and strictly read-only.

**2. Configuration & Performance**<br/>
Since ReadOnly stores are often used for static lists (like Dropdowns), performance optimization is critical.
* **Data Source & Alias:** Connects to the backend and defines the handle for UI binding, just like a standard store.
* **Singleton:** (Highly Recommended for Lookups). If you have a "Status List" used on 10 different pages, enabling Singleton ensures the data is fetched only once and shared across the entire application session.
* **Cache All:** If enabled, the store will cache query results client-side, preventing redundant network requests for static data.

**3. Strategic Use Cases**<br/>
* **Lookup Lists (LOVs):** Populating Select (Dropdown) components or Radio Groups.
* **Audit Logs:** Displaying a history of changes where the history itself must remain immutable.
* **Dashboards:** Powering read-only charts and KPI metrics.

**4. Best Practices**<br/>
* **The "Select" Component Warning:** If you are binding this store to a Select (Dropdown) component, **Turn Off Auto Query**.
    * **Reason:** The Select component has built-in logic to trigger a query when it renders. If Auto Query is also on, the store will fire two identical requests to the server unnecessarily.
* **Transitioning:** If you start with a ReadOnly store and later realize you need to edit the data, you can use the **Replace With** feature to convert it into a fully writable Data Store without breaking your alias bindings.`,
  };
};

export default ReadOnlyDataStoreManager;
